<template>
  <div
    class="flex flex-col items-start page-wrapper md:flex-row p-0 h-full flex-grow"
  >
    <section
      class="w-full h-full"
      :class="{
        'rounded-md py-24 md:pe-24': noBackground === false,
      }"
    >
      <header class="pb-10">
        <h2 class="heading-xs pb-5">Vad är ett Familjeabonnemang?</h2>
        <p class="py-4 max-w-4xl">
          Om du har eller skaffar ett mobilabonnemang med 20GB surf eller mer
          (även kallat huvudabonnemang), har du möjlighet att lägga till upp
          till fyra anslutningar för
          <span
            v-if="defaultfamilyConnectionPlanPriceReg"
            class="text-text-primary strike-through w-fit mr-4"
            >{{ defaultfamilyConnectionPlanPriceReg }} </span
          >{{ defaultfamilyConnectionPlanPrice }}
          kr/mån per familjeanslutning.
        </p>
      </header>
      <div
        class="py-64 rounded-md"
        :style="{
          background: `url(${familyBgImgSrc}) no-repeat center center / cover`,
        }"
        :aria-label="infoCardsTextForScreenReaders"
      >
        <div class="flex justify-evenly" aria-hidden="true">
          <div class="w-1/2 text-center">
            <div class="flex flex-col items-center gap-6">
              <BoxedText
                box-color-class="bg-surface-primary"
                element="p"
                text-class="heading-xxs"
              >
                <span class="font-xtrabold">30GB</span> Huvudabonnemang
              </BoxedText>
              <!-- class="heading-xs -mb-5 text-text-on-highlight"-->
            </div>
            <BoxedText
              box-color-class="bg-surface-highlight"
              element="p"
              text-class="heading-xxs"
              text-color-class="text-text-on-highlight"
              class="mt-12"
            >
              <span
                v-if="defaultFamilyPlanPriceReg"
                class="heading-xxs strike-through w-fit"
              >
                {{ defaultFamilyPlanPriceReg }}
              </span>
              {{ defaultFamilyPlanPrice }} kr/mån
            </BoxedText>
          </div>

          <nuxt-icon
            name="plus"
            class="text-[2.4rem] flex items-center px-5 text-text-primary-inverted dark:text-text-primary"
            :filled="false"
          />

          <div class="w-1/2 text-center">
            <BoxedText
              box-color-class="bg-surface-primary"
              element="p"
              text-class="heading-xxs"
            >
              <span class="font-xtrabold">30GB</span> Familjemedlem
            </BoxedText>
            <BoxedText
              box-color-class="bg-surface-highlight"
              element="p"
              text-class="heading-xxs"
              class="mt-12"
              text-color-class="text-text-on-highlight"
            >
              <span
                v-if="defaultfamilyConnectionPlanPriceReg"
                class="heading-xxs text-text-primary strike-through w-fit"
              >
                {{ defaultfamilyConnectionPlanPriceReg }}
              </span>
              {{ defaultfamilyConnectionPlanPrice }} kr/mån
            </BoxedText>
          </div>
        </div>
      </div>
      <div class="flex flex-col">
        <div class="flex flex-col mt-5 mb-8">
          <p>
            Exempel på ett 30 GB huvudabonnemang med ett 30 GB familjeabonnemang
            tillagd.
            <span class="heading-xxs">
              Alla får sin egna surfmängd på 30 GB var.
            </span>
          </p>
        </div>
      </div>
      <AppNotification type="info" class="w-full mt-20">
        <template #header>
          <nuxt-icon
            filled
            name="alert-circle-blue"
            class="text-[2.4rem]"
            aria-hidden="true"
          />
          <h4 class="heading-xxs">En ägare för alla abonnemang</h4>
        </template>
        <p>
          Personen som står på huvudabonnemanget blir också ägare och
          betalningsansvarig för familjeabonnemangen.
          <NuxtLink
            to="https://www.halebop.se/villkor-och-prislistor"
            target="_blank"
            aria-label="Se villkor för familjeabonnemang"
            external
          >
            Se villkor.
          </NuxtLink>
        </p>
      </AppNotification>
    </section>
  </div>
</template>

<script setup lang="ts">
interface Props {
  noBackground?: boolean;
}

withDefaults(defineProps<Props>(), {
  noBackground: false,
});

const familyStore = useFamilyStore();
const config = useRuntimeConfig();

const defaultFamilyPlan = computed(() => {
  return familyStore.familyPlanBundlePricesToDisplay.find((x) =>
    x.Plan?.PlanName.includes('30'),
  )?.Plan;
});

const defaultFamilyPlanPrice = computed(() => {
  return defaultFamilyPlan.value?.MonthlyFee;
});

const defaultFamilyPlanPriceReg = computed(() => {
  return defaultFamilyPlan.value?.RegularMonthlyFee;
});

const defaultfamilyConnectionPlan = computed(() => {
  return familyStore.getDefaultFamilyConnectionPlan?.bundlePrices.find(
    (x) => x.BindingPeriod.Months === 12,
  )?.Plan;
});

const defaultfamilyConnectionPlanPrice = computed(() => {
  return defaultfamilyConnectionPlan.value?.MonthlyFee;
});

const defaultfamilyConnectionPlanPriceReg = computed(() => {
  return defaultfamilyConnectionPlan.value?.RegularMonthlyFee;
});

const infoCardsTextForScreenReaders = computed(() => {
  const planPrice = defaultFamilyPlanPrice.value || 0;
  const childPlanPrice = defaultfamilyConnectionPlanPrice.value || 0;
  const sum = planPrice + childPlanPrice;

  return (
    `Illustration: 30 GB huvudabonnemang för ${planPrice} kronor i månaden, ` +
    `plus en 30 GB familjeanslutning för ${childPlanPrice} kronor i månaden, ` +
    `sammanlagt ${sum} kronor i månaden.`
  );
});

const familyBgImgSrc = computed(() => {
  let baseUrl = config.public.publicCloudfrontDistribution;
  let bucket = config.public.s3BucketEnvFolderPrefix;

  if (!baseUrl.endsWith('/')) {
    baseUrl += '/';
  }

  if (!bucket.endsWith('/')) {
    bucket += '/';
  }

  const url = `${baseUrl}${bucket}images/bg/family_excited.png`;

  try {
    new URL(url);
    return url;
  } catch (e) {
    console.warn('Url for background image invalid', url);
    return baseUrl;
  }
});
</script>

<style scoped></style>
